// ./src/components/features/PartnerApplications/PartnerApplicationDrawer/PartnerApplicationDrawer.tsx
//
// One sponsorship application, in full — the message, the contact details, the deal and the
// decision (#A4.5).
//
// ⚠ A URL STAGE, not a useState toggle: a reload, a shared link or a phone resuming from the
// background must reopen exactly this application. The page loads it BY ID so the drawer is
// self-sufficient on a cold reload — the list need not be present.
//
// ⚠ READ-ONLY except for the two decisions. Nothing here edits the application: a host does not get
// to rewrite what a sponsor said they wanted.
import React, { useCallback, useEffect, useState } from "react";
import { Stack, Group, Text, Badge, Anchor, Paper, Skeleton, Button, Alert } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, AppDrawer, TableDateTime } from "@/components/generic";
import { formatCurrency, stripeDashboardUrl } from "@/utils";
import {
  PartnerApplication,
  PARTNER_APPLICATION_STATUS,
  PARTNER_APPLICATION_STATUS_COLORS,
  partnerApplicationStatusLabel,
  partnerKindLabel,
  applicationWillRefund,
  applicationIsDecided,
} from "@/utils/api";
import {
  Payment,
  PaymentService,
  RsvpQuestion,
  RsvpQuestionAnswerService,
  QUESTION_ASKED_ON,
} from "@/utils/api";
// ⚠ The SHARED answers section + detail row (#A4.6) — the volunteer inbox reads answers the same way.
import { ApplicationAnswersSection, ApplicationDetailRow } from "@/components/features/Applications";
import {
  APPLICATION_APPROVE_ACTION,
  APPLICATION_REJECT_ACTION,
  APPLICATION_WITHDRAW_ACTION,
} from "@/hooks/usePartnerApplications";

interface PartnerApplicationDrawerProps {
  opened: boolean;
  onClose: () => void;
  application?: PartnerApplication | null;
  loading?: boolean;
  onApprove: (application: PartnerApplication) => void;
  onReject: (application: PartnerApplication) => void;
  /** The sponsor pulled out. ⚠ On an APPROVED one the host states whether money goes back. */
  onWithdraw: (application: PartnerApplication) => void;
  /** Opens the printable receipt in a new tab. */
  onPrintReceipt: (application: PartnerApplication) => void;
  isDeciding: (id?: string, actionKey?: string) => boolean;
}

/**
 * The money actually moved for this application: the charge, and any refund of it.
 *
 * ⚠ READ FROM THE LEDGER, not from the application's own columns. The application says what was
 * AGREED; the ledger says what a processor actually did, which is the only thing a host can
 * reconcile against their Stripe account — and the only place a refund's own id exists.
 */
const ApplicationMoney: React.FC<{ application: PartnerApplication; onPrint: () => void }> = React.memo(
  ({ application, onPrint }) => {
    const { t } = useTranslation(["partners"]);
    const [rows, setRows] = useState<Payment[]>([]);
    const [loading, setLoading] = useState(false);

    const applicationId = application.id;

    useEffect(() => {
      if (!applicationId) {
        return;
      }

      let cancelled = false;
      setLoading(true);

      PaymentService.list({ partnerApplicationId: [applicationId] })
        .then((res) => {
          if (!cancelled) {
            setRows(res.code === 0 && Array.isArray(res.data) ? (res.data as Payment[]) : []);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setRows([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });

      return () => {
        cancelled = true;
      };
    }, [applicationId]);

    if (loading) {
      return <Skeleton height={60} radius="md" />;
    }

    const charge = rows.find((r) => !r.isRefund) ?? null;
    const refunds = rows.filter((r) => r.isRefund);

    // ⚠ Nothing was ever taken — not a gap to fill with an empty panel. An `offline` or
    // `on_approval` sponsorship legitimately has no transaction yet.
    if (!charge && refunds.length === 0) {
      return null;
    }

    const chargeUrl = stripeDashboardUrl(charge);

    return (
      <Paper withBorder radius="md" p="md">
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Text size="md" fw={600}>
              {t("partners:applications.drawer.transactions", { defaultValue: "Transactions" })}
            </Text>
            <Button
              variant="subtle"
              size="compact-md"
              leftSection={<AppIcon iconName="Printer" size={16} />}
              onClick={onPrint}
            >
              {t("partners:applications.drawer.printReceipt", { defaultValue: "Print receipt" })}
            </Button>
          </Group>

          {charge ? (
            <Row label={t("partners:applications.drawer.charge", { defaultValue: "Charged" })}>
              <Text size="md" style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatCurrency(Number(charge.grossAmount ?? 0), charge.currencyCode || undefined)}
              </Text>
              {chargeUrl ? (
                <Anchor href={chargeUrl} target="_blank" rel="noopener noreferrer" size="md">
                  {t("partners:applications.drawer.viewInStripe", { defaultValue: "View in Stripe" })}
                </Anchor>
              ) : (
                // ⚠ Said out loud rather than rendering a dead or dangerous link.
                <Text size="sm" c="dimmed">
                  {t("partners:applications.drawer.noStripeLink", { defaultValue: "No Stripe link available" })}
                </Text>
              )}
            </Row>
          ) : null}

          {refunds.map((refund) => {
            const refundUrl = stripeDashboardUrl(refund);

            return (
              <Row
                key={refund.id}
                label={t("partners:applications.drawer.refunded", { defaultValue: "Refunded" })}
              >
                <Text size="md" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatCurrency(Number(refund.grossAmount ?? 0), refund.currencyCode || undefined)}
                </Text>
                {refundUrl ? (
                  <Anchor href={refundUrl} target="_blank" rel="noopener noreferrer" size="md">
                    {t("partners:applications.drawer.viewRefundInStripe", { defaultValue: "View refund in Stripe" })}
                  </Anchor>
                ) : (
                  <Text size="sm" c="dimmed">
                    {t("partners:applications.drawer.noStripeLink", { defaultValue: "No Stripe link available" })}
                  </Text>
                )}
              </Row>
            );
          })}
        </Stack>
      </Paper>
    );
  },
);

ApplicationMoney.displayName = "ApplicationMoney";

/**
 * What the applicant told the host — the SHARED answers section (#A4.6), narrowed to the kinds THIS application
 * was asked about: a question scoped to exhibitors was never put to a sponsor, so showing it as unanswered would be
 * a lie about what they were asked. A question with no kinds is asked of everybody.
 */
const ApplicationAnswers: React.FC<{ application: PartnerApplication }> = React.memo(({ application }) => {
  const { t } = useTranslation(["partners"]);
  const applicationId = application.id;
  const partnerKind = application.partnerKind;
  const loadAnswers = useCallback(() => RsvpQuestionAnswerService.listForApplication(String(applicationId)), [applicationId]);
  const questionFilter = useCallback(
    (q: RsvpQuestion) => {
      const kinds = Array.isArray(q.partnerKinds) ? (q.partnerKinds as string[]) : [];

      return kinds.length === 0 || (!!partnerKind && kinds.includes(partnerKind));
    },
    [partnerKind],
  );

  if (!applicationId) {
    return null;
  }

  return (
    <ApplicationAnswersSection
      eventId={application.eventId}
      askedOn={QUESTION_ASKED_ON.PARTNER_APPLICATION}
      loadAnswers={loadAnswers}
      questionFilter={questionFilter}
      title={t("partners:applications.drawer.answers", { defaultValue: "What they told you" })}
    />
  );
});

ApplicationAnswers.displayName = "ApplicationAnswers";

const Row = ApplicationDetailRow;

export const PartnerApplicationDrawer: React.FC<PartnerApplicationDrawerProps> = ({
  opened,
  onClose,
  application,
  loading,
  onApprove,
  onReject,
  onWithdraw,
  onPrintReceipt,
  isDeciding,
}) => {
  const { t } = useTranslation(["partners", "common"]);

  const showSkeleton = !!loading && !application?.id;
  const decided = application ? applicationIsDecided(application) : false;
  const willRefund = application ? applicationWillRefund(application) : false;

  // ⚠ The GAP, not `serviceFeeAmount` — under `absorb` there is still a fee, but the sponsor did not
  // pay it, and labelling their charge as fee-inclusive would be false.
  const serviceFee = application?.serviceFeeAmount != null ? Number(application.serviceFeeAmount) : null;
  const feePassedOn =
    application?.totalAmount != null && application?.amount != null && Number(application.totalAmount) > Number(application.amount)
      ? Math.round((Number(application.totalAmount) - Number(application.amount)) * 100) / 100
      : null;
  const sponsorPaidMore = feePassedOn !== null;
  const approving = isDeciding(application?.id, APPLICATION_APPROVE_ACTION);
  const rejecting = isDeciding(application?.id, APPLICATION_REJECT_ACTION);
  const withdrawing = isDeciding(application?.id, APPLICATION_WITHDRAW_ACTION);
  // ⚠ Pending OR approved. A declined or already-withdrawn application has nothing to pull.
  const isWithdrawable =
    !!application &&
    application.applicationStatus !== PARTNER_APPLICATION_STATUS.WITHDRAWN &&
    application.applicationStatus !== PARTNER_APPLICATION_STATUS.REJECTED;
  const busy = isDeciding(application?.id);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("partners:applications.drawer.title", { defaultValue: "Sponsorship application" })}
      position="right"
      size="lg"
      closable={!busy}
      // The footer offers only Close — the decisions live in the body, next to what they decide on.
      actionType="close"
    >
      {showSkeleton ? (
        <Stack gap="md">
          <Skeleton height={28} width="60%" />
          <Skeleton height={80} />
          <Skeleton height={120} />
        </Stack>
      ) : !application?.id ? (
        <Text size="md" c="dimmed">
          {t("partners:applications.drawer.gone", { defaultValue: "That application is no longer here." })}
        </Text>
      ) : (
        <Stack gap="lg">
          <Stack gap={4}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Text size="xl" fw={700} style={{ overflowWrap: "anywhere" }}>
                {application.orgName || "—"}
              </Text>
              <Badge
                size="md"
                variant="light"
                color={PARTNER_APPLICATION_STATUS_COLORS[application.applicationStatus || ""] || "gray"}
              >
                {partnerApplicationStatusLabel(application.applicationStatus)}
              </Badge>
            </Group>
            <Text size="md" c="dimmed">
              {partnerKindLabel(application.partnerKind)}
            </Text>
          </Stack>

          <Paper withBorder radius="md" p="md">
            <Stack gap="sm">
              <Row label={t("partners:applications.drawer.price", { defaultValue: "Agreed price" })}>
                <Text size="md" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                  {application.amount != null
                    ? formatCurrency(Number(application.amount), application.currencyCode || undefined)
                    : "—"}
                </Text>
                {/* ⚠ The price is what they were QUOTED, not today's. A host who re-priced the
                    package after they applied still owes them this number. */}
                <Text size="sm" c="dimmed">
                  {t("partners:applications.drawer.priceHint", { defaultValue: "quoted when they applied" })}
                </Text>
              </Row>

              {/* ⚠ THREE DIFFERENT NUMBERS, and a host who sees only one of them cannot reconcile
                  their Stripe account. `amount` is what they are owed, `totalAmount` is what the
                  sponsor's card was charged, and the gap is our service fee. They are equal under
                  `absorb`, where our fee comes out of the host's share instead — so this row only
                  appears when the sponsor really did pay more. */}
              {sponsorPaidMore ? (
                <Row label={t("partners:applications.drawer.charged", { defaultValue: "Sponsor charged" })}>
                  <Text size="md" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatCurrency(Number(application.totalAmount), application.currencyCode || undefined)}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {t("partners:applications.drawer.chargedHint", {
                      defaultValue: "includes {{fee}} service fee",
                      fee: formatCurrency(feePassedOn as number, application.currencyCode || undefined),
                    })}
                  </Text>
                </Row>
              ) : null}

              {serviceFee !== null ? (
                <Row label={t("partners:applications.drawer.serviceFee", { defaultValue: "Service fee" })}>
                  <Text size="md" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatCurrency(serviceFee, application.currencyCode || undefined)}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {sponsorPaidMore
                      ? t("partners:applications.drawer.serviceFeePaidBySponsor", { defaultValue: "paid by the sponsor" })
                      : t("partners:applications.drawer.serviceFeeAbsorbed", { defaultValue: "taken from your share" })}
                  </Text>
                </Row>
              ) : null}

              <Row label={t("partners:applications.drawer.payment", { defaultValue: "Payment" })}>
                {willRefund ? (
                  <Badge size="md" variant="light" color="teal">
                    {t("partners:applications.paid", { defaultValue: "Paid" })}
                  </Badge>
                ) : (
                  <Text size="md">{t("partners:applications.drawer.notPaid", { defaultValue: "Nothing taken yet" })}</Text>
                )}
              </Row>

              <Row label={t("partners:applications.submitted", { defaultValue: "Applied" })}>
                <TableDateTime value={application.submittedAt} inline />
              </Row>

              {application.reviewedAt ? (
                <Row label={t("partners:applications.drawer.reviewed", { defaultValue: "Decided" })}>
                  <TableDateTime value={application.reviewedAt} inline />
                </Row>
              ) : null}
            </Stack>
          </Paper>

          <ApplicationMoney application={application} onPrint={() => onPrintReceipt(application)} />

          <ApplicationAnswers application={application} />

          <Paper withBorder radius="md" p="md">
            <Stack gap="sm">
              {/* ⚠ Shown as two rows, not recombined. The whole reason for splitting them is that
                  the two parts are separately useful — the person record built on approval needs
                  them apart, and a host addressing a sponsor needs the first name alone. */}
              {application.firstName ? (
                <Row label={t("partners:applications.drawer.firstName", { defaultValue: "First name" })}>
                  <Text size="md">{application.firstName}</Text>
                </Row>
              ) : null}
              {application.lastName ? (
                <Row label={t("partners:applications.drawer.lastName", { defaultValue: "Last name" })}>
                  <Text size="md">{application.lastName}</Text>
                </Row>
              ) : null}
              {application.email ? (
                <Row label={t("common:fields.email", { defaultValue: "Email" })}>
                  <Anchor href={`mailto:${application.email}`} size="md">
                    {application.email}
                  </Anchor>
                </Row>
              ) : null}
              {application.phone ? (
                <Row label={t("common:fields.phone", { defaultValue: "Phone" })}>
                  <Anchor href={`tel:${application.phone}`} size="md">
                    {application.phone}
                  </Anchor>
                </Row>
              ) : null}
              {application.websiteUrl ? (
                <Row label={t("common:fields.website", { defaultValue: "Website" })}>
                  <Anchor href={application.websiteUrl} target="_blank" rel="noopener noreferrer" size="md">
                    {application.websiteUrl}
                  </Anchor>
                </Row>
              ) : null}
            </Stack>
          </Paper>

          {application.message ? (
            <Stack gap={4}>
              <Text size="md" c="dimmed">
                {t("partners:applications.drawer.message", { defaultValue: "What they said" })}
              </Text>
              <Paper withBorder radius="md" p="md">
                <Text size="md" style={{ whiteSpace: "pre-line" }}>
                  {application.message}
                </Text>
              </Paper>
            </Stack>
          ) : null}

          {application.reviewNotes ? (
            <Stack gap={4}>
              <Text size="md" c="dimmed">
                {t("partners:applications.drawer.reviewNotes", { defaultValue: "Your note" })}
              </Text>
              <Paper withBorder radius="md" p="md">
                <Text size="md" style={{ whiteSpace: "pre-line" }}>
                  {application.reviewNotes}
                </Text>
              </Paper>
            </Stack>
          ) : null}

          {/* ⚠ WITHDRAWAL IS NOT A DECISION, so it sits outside the decided/undecided split: a
              sponsor can pull out before OR after approval, and an approved one has a place and a
              partner row to undo. It is hidden only once the application is already withdrawn or
              declined, where there is nothing left to pull. */}
          {isWithdrawable ? (
            <Group justify="flex-start">
              <Button
                variant="subtle"
                color="gray"
                loading={withdrawing}
                disabled={busy && !withdrawing}
                onClick={() => onWithdraw(application)}
                size="md"
                leftSection={<AppIcon iconName="ArrowBackUp" size={16} />}
              >
                {t("partners:applications.actions.withdraw", { defaultValue: "They withdrew" })}
              </Button>
            </Group>
          ) : null}

          {decided ? (
            <Alert color="gray" variant="light" icon={<AppIcon iconName="Check" size={18} />}>
              {t("partners:applications.drawer.decidedNote", {
                defaultValue: "This application has been decided. Manage the partner on the Sponsors & Partners page.",
              })}
            </Alert>
          ) : (
            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                color="red"
                loading={rejecting}
                disabled={busy && !rejecting}
                onClick={() => onReject(application)}
                size="md"
              >
                {willRefund
                  ? t("partners:applications.actions.declineRefund", { defaultValue: "Decline and refund" })
                  : t("partners:applications.actions.decline", { defaultValue: "Decline" })}
              </Button>
              <Button
                color="green"
                loading={approving}
                disabled={busy && !approving}
                onClick={() => onApprove(application)}
                size="md"
              >
                {t("partners:applications.actions.approve", { defaultValue: "Approve" })}
              </Button>
            </Group>
          )}
        </Stack>
      )}
    </AppDrawer>
  );
};
